import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import riderService, { resolveRiderServiceUrl } from '../../services/riderService';

interface AssignmentRow {
  riderId?: string;
  vehicleId?: string;
  hubId?: string;
  securityDepositConfigId?: string;
  assignedAt?: string;
  registrationNumber?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleModelId?: string;
  batteryConfig?: string;
  dailyRent?: string | number;
  startMileage?: string | number;
  batteryPercentageStart?: string | number;
  conditionOnAssign?: string;
  notes?: string;
}

const normalizeCell = (value: unknown) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const normalizeHeader = (key: string) =>
  key
    .trim()
    .replace(/\s+/g, '')
    .replace(/[-_]/g, '')
    .toLowerCase();

const pickValue = (row: Record<string, unknown>, keys: string[]) => {
  const normalizedRow = Object.fromEntries(
    Object.entries(row).map(([key, value]) => [normalizeHeader(key), value]),
  );

  for (const key of keys) {
    const value = normalizedRow[normalizeHeader(key)];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const sheetToAssignments = (rows: Record<string, unknown>[]): AssignmentRow[] => {
  return rows
    .filter((row) => Object.values(row).some((value) => value !== null && value !== undefined && String(value).trim() !== ''))
    .map((row) => ({
      riderId: normalizeCell(pickValue(row, ['Rider ID', 'riderId', 'RiderId', 'Rider', 'riderID'])),
      vehicleId: normalizeCell(pickValue(row, ['Vehicle ID', 'vehicleId', 'VehicleId', 'Vehicle', 'vehicleID'])),
      hubId: normalizeCell(pickValue(row, ['Hub ID', 'hubId', 'HubId', 'Hub', 'hubID'])),
      securityDepositConfigId: normalizeCell(pickValue(row, ['Security Deposit Config ID', 'SecurityDepositConfigId', 'securityDepositConfigId', 'securityDepositconfigid', 'security deposit config id'])),
      assignedAt: normalizeCell(pickValue(row, ['Assigned At', 'assignedAt', 'Assignment Date', 'Date'])),
      registrationNumber: normalizeCell(pickValue(row, ['Registration Number', 'registrationNumber', 'Registration'])),
      vehicleMake: normalizeCell(pickValue(row, ['Vehicle Make', 'vehicleMake', 'Make'])),
      vehicleModel: normalizeCell(pickValue(row, ['Vehicle Model', 'vehicleModel', 'Model'])),
      vehicleModelId: normalizeCell(pickValue(row, ['Vehicle Model ID', 'vehicleModelId', 'Model ID'])),
      batteryConfig: normalizeCell(pickValue(row, ['Battery Config', 'batteryConfig', 'Battery Configuration', 'Battery'])),
      dailyRent: normalizeCell(pickValue(row, ['Daily Rent', 'dailyRent', 'Rent'])),
      startMileage: normalizeCell(pickValue(row, ['Start Mileage', 'startMileage', 'Mileage'])),
      batteryPercentageStart: normalizeCell(pickValue(row, ['Battery Percentage Start', 'batteryPercentageStart', 'Battery % Start', 'Battery %'])),
      conditionOnAssign: normalizeCell(pickValue(row, ['Condition On Assign', 'conditionOnAssign', 'Condition'])),
      notes: normalizeCell(pickValue(row, ['Notes', 'notes', 'Note'])),
    }));
};

const BulkVehicleAssignmentUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<AssignmentRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [errorReportUrl, setErrorReportUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const previewCount = useMemo(() => previewRows.length, [previewRows]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const allowed = ['.xlsx', '.xls'];
    const lowerName = selectedFile.name.toLowerCase();
    const isAllowed = allowed.some((ext) => lowerName.endsWith(ext));

    if (!isAllowed) {
      setError('Please upload a valid Excel file (.xlsx or .xls).');
      return;
    }

    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });
      const mappedRows = sheetToAssignments(rows);

      setFile(selectedFile);
      setPreviewRows(mappedRows);
      setError('');
      setErrorReportUrl(null);
      setMessage(`Loaded ${mappedRows.length} row(s) from ${selectedFile.name}.`);
    } catch (uploadError) {
      console.error(uploadError);
      setError('The file could not be parsed as a valid Excel sheet. Please check the format.');
    }
  };

  const downloadTemplate = () => {
    const templateRows = [
      {
        'Rider ID': 'RIDER_ID_1',
        'Vehicle ID': 'VEHICLE_ID_1',
        'Hub ID': 'HUB_ID_1',
        'Security Deposit Config ID': 'SD_CONFIG_ID_1',
        'Assigned At': '23-07-2026',
        'Registration Number': 'KA01AB1234',
        'Vehicle Make': 'Ola',
        'Vehicle Model': 'S1',
        'Vehicle Model ID': 'MODEL_ID_1',
        'Battery Config': '72V',
        'Daily Rent': 550,
        'Start Mileage': 1250,
        'Battery Percentage Start': 95,
        'Condition On Assign': 'Good',
        'Notes': 'Bulk assignment test',
      },
      {
        'Rider ID': 'RIDER_ID_2',
        'Vehicle ID': 'VEHICLE_ID_2',
        'Hub ID': 'HUB_ID_2',
        'Security Deposit Config ID': 'SD_CONFIG_ID_2',
        'Assigned At': '23-07-2026',
        'Registration Number': 'KA02CD5678',
        'Vehicle Make': 'Ather',
        'Vehicle Model': '450X',
        'Vehicle Model ID': 'MODEL_ID_2',
        'Battery Config': '60V',
        'Daily Rent': 600,
        'Start Mileage': 980,
        'Battery Percentage Start': 88,
        'Condition On Assign': 'Fair',
        'Notes': 'Bulk assignment test 2',
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bulk Assignments');
    XLSX.writeFile(workbook, 'bulk_vehicle_assignment_template.xlsx');
  };

  const startBulkAssignment = async () => {
    if (!file || previewRows.length === 0) {
      setError('Please upload a valid Excel file first.');
      return;
    }

    const assignments = previewRows.map((row) => ({
      riderId: row.riderId,
      vehicleId: row.vehicleId,
      hubId: row.hubId || undefined,
      securityDepositConfigId: row.securityDepositConfigId || undefined,
      assignedAt: row.assignedAt || undefined,
      registrationNumber: row.registrationNumber || undefined,
      vehicleMake: row.vehicleMake || undefined,
      vehicleModel: row.vehicleModel || undefined,
      vehicleModelId: row.vehicleModelId || undefined,
      batteryConfig: row.batteryConfig || undefined,
      dailyRent: row.dailyRent ? Number(row.dailyRent) : undefined,
      startMileage: row.startMileage ? Number(row.startMileage) : undefined,
      batteryPercentageStart: row.batteryPercentageStart ? Number(row.batteryPercentageStart) : undefined,
      conditionOnAssign: row.conditionOnAssign || undefined,
      notes: row.notes || undefined,
    }));

    const invalidRows = assignments
      .map((assignment, index) => ({
        index,
        assignment,
      }))
      .filter(({ assignment }) => !assignment.riderId || !assignment.vehicleId);

    if (invalidRows.length > 0) {
      setError('Each row must include both riderId and vehicleId.');
      return;
    }

    try {
      setUploading(true);
      setProgress(20);
      setError('');
      setMessage('Submitting bulk assignment request...');

      const response = await riderService.bulkAssignVehiclesToRiders(assignments);

      setProgress(100);
      const rawReportUrl = response?.data?.errorFileDetails?.downloadUrl || null;
      const reportUrl = rawReportUrl
        ? resolveRiderServiceUrl(rawReportUrl)
        : null;
      setErrorReportUrl(reportUrl);
      setMessage(
        `Bulk assignment completed: ${response?.data?.summary?.successful ?? 0} successful, ${(response?.data?.summary?.failed ?? 0)} failed.`,
      );
    } catch (submitError: any) {
      console.error(submitError);
      setError(submitError?.message || 'Bulk assignment failed.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Bulk Vehicle Assignment Upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload an Excel sheet with rider and vehicle details, preview the parsed rows, and submit them through the admin bulk assignment API.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
            >
              Select Excel File
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
              />
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
            >
              Download Template
            </Button>

            <Button
              variant="contained"
              color="success"
              onClick={startBulkAssignment}
              disabled={!file || uploading || previewRows.length === 0}
            >
              {uploading ? 'Submitting...' : 'Submit Bulk Assignments'}
            </Button>
          </Stack>

          {progress > 0 && <LinearProgress variant="determinate" value={progress} />}

          {error && <Alert severity="error">{error}</Alert>}
          {message && <Alert severity="info">{message}</Alert>}
          {errorReportUrl && (
            <Alert severity="warning">
              Some rows failed. Download the error report{' '}
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={async () => {
                  try {
                    setMessage('Downloading error report...');
                    // Use service to download with auth and save as file
                    const result = await riderService.downloadUrlAsBlob(errorReportUrl);
                   const blob = new Blob([result.data], {
                        type: String(result.contentType ?? "")
                    });
                    const downloadName = result.filename || 'bulk-assignment-errors.xlsx';
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = downloadName;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(link.href);
                    setMessage('Error report downloaded');
                  } catch (downloadError: any) {
                    console.error(downloadError);
                    setError(downloadError?.message || 'Failed to download error report');
                  }
                }}
              >
                Download
              </Button>
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Parsed rows: <Chip label={previewCount} size="small" color="primary" />
            </Typography>
          </Box>

          {previewRows.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rider ID</TableCell>
                    <TableCell>Vehicle ID</TableCell>
                    <TableCell>Hub ID</TableCell>
                    <TableCell>Security Deposit Config ID</TableCell>
                    <TableCell>Assigned At</TableCell>
                    <TableCell>Registration</TableCell>
                    <TableCell>Vehicle Model</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.slice(0, 8).map((row, index) => (
                    <TableRow key={`${row.riderId ?? 'row'}-${index}`}>
                      <TableCell>{row.riderId || '-'}</TableCell>
                      <TableCell>{row.vehicleId || '-'}</TableCell>
                      <TableCell>{row.hubId || '-'}</TableCell>
                      <TableCell>{row.securityDepositConfigId || '-'}</TableCell>
                      <TableCell>{row.assignedAt || '-'}</TableCell>
                      <TableCell>{row.registrationNumber || '-'}</TableCell>
                      <TableCell>{row.vehicleModel || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider />

          <Typography variant="caption" color="text.secondary">
            Expected Excel columns: Rider ID, Vehicle ID, Hub ID, Security Deposit Config ID, Assigned At, Registration Number, Vehicle Make, Vehicle Model, Vehicle Model ID, Battery Config, Daily Rent, Start Mileage, Battery Percentage Start, Condition On Assign, Notes.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BulkVehicleAssignmentUpload;
